/**
 * The saas-auth `/api/auth/**` surface Brand Center uses (genesis handoff §3).
 *
 * **Envelope contract, per endpoint** — deliberately not uniform:
 *  - `POST /api/auth/refresh` is read **FLAT only**. The evidence is one-sided (handoff §5,
 *    and saas-admin's shipped client reads the flat body), and — decisively — the vendored
 *    factory's internal `runRefresh` also reads it flat. Both refresh paths must agree on
 *    the wire format, or a tolerance here would mask a reactive-refresh failure: boot would
 *    succeed while every 401 retry silently bounced the user to login instead.
 *  - `POST /api/auth/session/exchange` and `GET /api/auth/me` go through `unwrapAuthBody`,
 *    which accepts flat or `{ success, data }`. `/me` is enveloped in saas-admin but shown
 *    bare in the handoff; the exchange is a new endpoint (SASATH-0157) neither handoff pins.
 *    Each has exactly one code path, so the tolerance is wholly exercised (both branches are
 *    tested) rather than half-covered.
 */
import { apiClient, LOGOUT_PATH, ME_PATH, REFRESH_PATH, SESSION_EXCHANGE_PATH } from './client';
import { refreshRequestConfig } from './apiAxiosInstance';
import type { ApiResponse, AuthTokenResponse, MeResponse } from './types';

/** Accept a flat body or a `{ success, data }` envelope. NOT used for refresh — see above. */
function unwrapAuthBody<T>(body: T | ApiResponse<T>): T {
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as ApiResponse<T>).data;
  }
  return body as T;
}

export const authApi = {
  /**
   * Silent refresh against the HttpOnly cookie. Marked as a refresh so a `401` (the
   * ordinary "no live session" case) resolves as a failed boot instead of being re-entered
   * by the factory's silent-refresh interceptor. Flat body — same as the factory's own
   * `runRefresh`, see the envelope contract above.
   */
  async refresh(): Promise<AuthTokenResponse> {
    const { data } = await apiClient.post<AuthTokenResponse>(
      REFRESH_PATH,
      {},
      refreshRequestConfig(),
    );
    return data;
  },

  /** Code-delivery mode (SASATH-0157): trade the opaque code for a session. */
  async exchangeCode(code: string): Promise<AuthTokenResponse> {
    const { data } = await apiClient.post<AuthTokenResponse | ApiResponse<AuthTokenResponse>>(
      SESSION_EXCHANGE_PATH,
      { code },
    );
    return unwrapAuthBody<AuthTokenResponse>(data);
  },

  /** Identity bootstrap. Returns either the tenant or the platform-operator shape. */
  async me(): Promise<MeResponse> {
    const { data } = await apiClient.get<MeResponse | ApiResponse<MeResponse>>(ME_PATH);
    return unwrapAuthBody<MeResponse>(data);
  },

  /** Clears the scoped session server-side (requires a valid `Origin`). */
  async logout(): Promise<void> {
    await apiClient.post(LOGOUT_PATH, {});
  },
};
